import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Plane, Hotel, CarFront, Camera, Star, ArrowRight } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import { inr } from "@/api";

const DURATIONS = [
  { id: "any", label: "Any duration", test: (p) => true },
  { id: "short", label: "Up to 5 days", test: (p) => p.days <= 5 },
  { id: "mid", label: "6–7 days", test: (p) => p.days >= 6 && p.days <= 7 },
  { id: "long", label: "8+ days", test: (p) => p.days >= 8 },
];

export default function PackagesSection({ packages, destinations, filter, setFilter, onEnquire }) {
  const filtered = useMemo(() => {
    const dur = DURATIONS.find((d) => d.id === filter.duration) || DURATIONS[0];
    return packages.filter((p) =>
      (filter.destination === "All" || p.destination === filter.destination) && dur.test(p));
  }, [packages, filter]);

  return (
    <section id="packages" className="scroll-mt-24 py-24 sm:py-32 bg-onyx-800/40 border-y border-white/5" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Kicker>Signature Packages</Kicker>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-50 tracking-tight max-w-xl">
              Journeys, <em className="gold-text not-italic">all-inclusive</em> by design
            </h2>
            <p className="text-slate-400 text-sm max-w-sm">
              Every price covers flights, hotels, transfers and sightseeing. No hidden extras, ever.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="package-filters">
            {["All", ...destinations.map((d) => d.name)].map((name) => (
              <button key={name}
                onClick={() => setFilter((f) => ({ ...f, destination: name }))}
                data-testid={`filter-destination-${name.toLowerCase()}`}
                className={`rounded-full px-5 py-2 text-xs font-mono uppercase tracking-wider border transition-colors ${
                  filter.destination === name
                    ? "bg-gold text-onyx-900 border-gold font-semibold"
                    : "border-white/15 text-slate-300 hover:border-gold/60 hover:text-gold"}`}>
                {name}
              </button>
            ))}
            <select value={filter.duration}
              onChange={(e) => setFilter((f) => ({ ...f, duration: e.target.value }))}
              data-testid="package-filter-duration-select"
              className="ml-auto rounded-full bg-onyx-700 border border-white/15 px-5 py-2 text-xs font-mono uppercase tracking-wider text-slate-200 focus:outline-none focus:border-gold/60">
              {DURATIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="packages-grid">
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <article className="group h-full rounded-2xl overflow-hidden bg-onyx-800 border border-white/10 hover:border-gold/50 transition-colors duration-500 flex flex-col"
                data-testid={`package-card-${p.id}`}>
                <div className="relative h-56 overflow-hidden">
                  <img src={p.image} alt={p.name} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-onyx-800 via-transparent to-transparent" />
                  {p.tag && (
                    <span className="absolute top-4 left-4 rounded-full bg-onyx-900/80 backdrop-blur px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/30">
                      {p.tag}
                    </span>
                  )}
                  {p.featured && (
                    <span className="absolute top-4 right-4 rounded-full bg-gold px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-onyx-900 font-semibold">
                      Bestseller
                    </span>
                  )}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                    {p.destination} · {p.nights}N / {p.days}D
                  </div>
                  <h3 className="mt-2 font-serif text-xl text-slate-50 leading-snug">{p.name}</h3>
                  <div className="mt-4 flex items-center gap-4 text-slate-400">
                    {[Plane, Hotel, CarFront, Camera].map((Icon, j) => (
                      <span key={j} className="flex items-center gap-1.5 text-[11px]"><Icon size={14} className="text-gold" /></span>
                    ))}
                    <span className="ml-auto flex items-center gap-1 text-xs text-slate-300">
                      <Star size={13} className="fill-gold text-gold" /> {p.rating} <span className="text-slate-500">({p.reviews})</span>
                    </span>
                  </div>
                  <div className="mt-5 pt-5 border-t border-white/10 flex items-end justify-between mt-auto">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">per person</div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-serif text-2xl text-gold">{inr(p.price)}</span>
                        {p.old_price && <span className="text-xs text-slate-500 line-through">{inr(p.old_price)}</span>}
                      </div>
                    </div>
                    <Link to={`/packages/${p.id}`} data-testid={`package-card-detail-button-${p.id}`}
                      className="group/link flex items-center gap-1.5 text-xs font-semibold text-slate-200 hover:text-gold transition-colors">
                      View itinerary <ArrowRight size={14} className="transition-transform group-hover/link:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="mt-16 text-center text-slate-400" data-testid="packages-empty">No packages match these filters yet — try widening your search.</p>
        )}
      </div>
    </section>
  );
}
